/**
 * Launch-day celebration bar.
 *
 * Visible ONLY during the 24-hour window starting at
 * `2026-06-03T19:00:00Z` (12:00 PM PDT launch instant), and ONLY on the
 * creator-application paths (/submit, /sell, /list, /apply). The homepage
 * and every other route stay clean — users have to click Apply/Sell to
 * see the Founding Creator counter and CTA, per founder direction.
 * After 24h, the bar stops rendering completely — no admin action needed.
 *
 * Auto-hides itself for visitors who dismiss (localStorage memory).
 *
 * Reads the live "spots remaining" count from /api/public/founding-seats
 * so the urgency number is honest and updates as creators apply.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
// Production launch instant: 2026-06-03T19:00:00Z = 12:00 PM PDT (noon Pacific, in DST).
// Bar renders for the 24h window starting at this instant, then stops.
const LAUNCH_AT_MS = new Date('2026-06-03T19:00:00Z').getTime();
const WINDOW_MS = 24 * 60 * 60 * 1000; // 24h post-launch
const STORAGE_KEY = 'tal_launchday_bar_dismissed_v1';

export default function LaunchDayBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [spots, setSpots] = useState(null);

  // Per founder request (June 1 2026): the launch-day urgency banner must
  // ONLY appear on the creator-application surface, not on the homepage or
  // anywhere else. Users have to click "Apply" / "Sell your AI tool" to see
  // the Founding Creator counter — keeping the homepage focused on browsing.
  const SHOW_ON_PATHS = ['/submit', '/sell', '/list', '/apply'];
  const showOnPath = SHOW_ON_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const now = Date.now();
    const inWindow = now >= LAUNCH_AT_MS && now < LAUNCH_AT_MS + WINDOW_MS;
    const dismissed = !!window.localStorage?.getItem(STORAGE_KEY);
    setVisible(inWindow && !dismissed && showOnPath);
  }, [showOnPath]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        // Same endpoint the ComingSoon hero badge already calls — keeps
        // the two numbers in sync without a second source of truth.
        const res = await axios.get(`${API}/api/public/founding-seats`);
        if (!cancelled && typeof res.data?.remaining === 'number') {
          setSpots(res.data.remaining);
        }
      } catch (_) {
        // Non-fatal — bar still renders without the number
      }
    })();
    return () => { cancelled = true; };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    try { window.localStorage?.setItem(STORAGE_KEY, '1'); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  const headline = spots > 0
    ? `We're live · First 100 Founding Creator spots filling up — ${spots} left`
    : spots === 0
      ? `We're live · Founding Creator program is full — join the waitlist`
      : `We're live · Founding Creator spots filling up`;

  return (
    <>
      <div
        role="region"
        aria-label="Launch day announcement"
        data-testid="launch-day-bar"
        className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-[#C9A227] via-[#00F0FF] to-[#C9A227] text-black"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span className="truncate text-xs sm:text-sm font-semibold">
              <span aria-hidden className="mr-1.5">🎉</span>
              {headline}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => navigate(spots === 0 ? '/?audience=buyer' : '/submit')}
              data-testid="launch-day-cta"
              className="text-xs sm:text-sm font-bold whitespace-nowrap underline underline-offset-2 hover:no-underline"
            >
              {spots === 0 ? 'Join waitlist' : 'Apply now'} →
            </button>
            <button
              onClick={dismiss}
              aria-label="Dismiss launch day banner"
              data-testid="launch-day-dismiss"
              className="text-black/60 hover:text-black px-1.5 ml-1"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
      {/* Spacer to push the rest of the page below the fixed bar so the
          existing nav (which is also position: fixed top:0 on most live
          pages) doesn't sit underneath us. ~36px = bar height. */}
      <div aria-hidden className="h-9" />
    </>
  );
}
